<script lang="ts">
  import { router } from '@inertiajs/svelte';

  interface AttendanceSummary {
    yes: number; no: number; maybe: number; unknown: number; total: number;
  }
  interface Meeting {
    id: string; date: string; label: string | null;
    title: string | null; location: string | null; notes: string | null;
    cancelled: boolean; summary: AttendanceSummary;
  }
  interface AttendanceRow {
    response_id: string;
    attendance_id: string | null;
    name: string;
    threads: string | null;
    phone: string | null;
    role: string | null;
    tier: string | null;
    rsvp: 'yes' | 'no' | 'maybe' | 'unknown';
    note: string | null;
    responded_at: string | null;
  }
  interface Props {
    team: {
      slug: string; name: string;
      kakao_url: string | null; kakao_code: string | null;
      member_count: number;
    };
    year: number; month: number;
    meetings: Meeting[];
    selected_date: string | null;
    selected_meeting: Meeting | null;
    attendance: AttendanceRow[];
  }

  let { team, year, month, meetings, selected_date, selected_meeting, attendance }: Props = $props();

  let attendanceState = $state<AttendanceRow[]>(attendance);

  // ===== Calendar =====
  function daysInMonth(y: number, m: number): number {
    return new Date(y, m, 0).getDate();
  }
  function firstWeekday(y: number, m: number): number {
    return new Date(y, m - 1, 1).getDay(); // 0=Sun..6=Sat
  }

  let weeks = $derived.by(() => {
    const first = firstWeekday(year, month);
    const total = daysInMonth(year, month);
    const cells: Array<{ day: number | null; iso: string | null }> = [];
    for (let i = 0; i < first; i++) cells.push({ day: null, iso: null });
    for (let d = 1; d <= total; d++) {
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, iso });
    }
    while (cells.length % 7 !== 0) cells.push({ day: null, iso: null });
    const w: typeof cells[] = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  });

  let meetingByDate = $derived(
    Object.fromEntries(meetings.map(m => [m.date, m]))
  );

  function navigateMonth(delta: number) {
    let y = year, m = month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    router.get(`/admin/community/study_teams/${team.slug}/attendance`, { year: y, month: m, date: selected_date ?? '' });
  }

  function selectDate(iso: string | null) {
    if (!iso) return;
    router.get(`/admin/community/study_teams/${team.slug}/attendance`, { year, month, date: iso });
  }

  const todayIso = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();

  // ===== RSVP toggling =====
  const rsvpLabels: Record<string, string> = {
    yes: '✅ 참석', no: '❌ 불참', maybe: '⏰ 미정', unknown: '❓ 응답없음'
  };
  const rsvpColors: Record<string, string> = {
    yes: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    no: 'bg-rose-100 text-rose-800 border-rose-300',
    maybe: 'bg-amber-100 text-amber-800 border-amber-300',
    unknown: 'bg-gray-100 text-gray-700 border-gray-300'
  };
  const tierDot: Record<string, string> = {
    blue: 'bg-blue-500', green: 'bg-green-500', yellow: 'bg-yellow-400'
  };

  function getCsrf(): string {
    const tag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
    return tag?.content ?? '';
  }

  async function setRsvp(row: AttendanceRow, rsvp: 'yes' | 'no' | 'maybe' | 'unknown') {
    if (!selected_meeting) return;
    const orig = row.rsvp;
    row.rsvp = rsvp;
    const res = await fetch(`/admin/community/team_meeting_attendances/${row.response_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': getCsrf() },
      body: JSON.stringify({
        meeting_id: selected_meeting.id,
        response_id: row.response_id,
        rsvp
      })
    });
    if (!res.ok) {
      row.rsvp = orig;
      alert('업데이트 실패');
      return;
    }
    const data = await res.json();
    row.attendance_id = data.attendance_id;
    row.responded_at = data.responded_at;
  }

  // Live summary recompute
  let liveSummary = $derived.by(() => {
    const s = { yes: 0, no: 0, maybe: 0, unknown: 0, total: attendanceState.length };
    attendanceState.forEach(r => { (s as any)[r.rsvp] += 1; });
    return s;
  });

  // ===== Add meeting =====
  let newDate = $state('');
  let newLabel = $state('19:00');
  let newTitle = $state('');
  async function createMeeting() {
    if (!newDate) return;
    const res = await fetch(`/admin/community/study_teams/${team.slug}/meetings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': getCsrf() },
      body: JSON.stringify({
        meeting: {
          meeting_date: newDate, starts_at_label: newLabel,
          title: newTitle || null, location: '한국거래소 세미나실'
        }
      })
    });
    if (res.ok) {
      newDate = ''; newTitle = '';
      router.reload();
    } else {
      alert('생성 실패');
    }
  }
</script>

<div class="p-6 space-y-6">
  <header>
    <a href="/admin/community/study_teams" class="text-sm text-gray-600 hover:underline">← 팀 목록</a>
    <div class="flex items-baseline gap-3 mt-1">
      <h1 class="text-2xl font-bold">{team.name}</h1>
      <span class="text-sm text-gray-600">총 {team.member_count}명</span>
      {#if team.kakao_url}
        <a href={team.kakao_url} target="_blank" rel="noopener noreferrer"
           class="text-xs text-blue-600 hover:underline ml-auto">
          오픈톡방 (코드 {team.kakao_code ?? '-'}) →
        </a>
      {/if}
    </div>
  </header>

  <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
    <!-- 좌: 캘린더 -->
    <div class="lg:col-span-2 bg-white border rounded-lg p-4">
      <div class="flex items-center justify-between mb-3">
        <button onclick={() => navigateMonth(-1)} class="px-2 py-1 text-sm hover:bg-gray-100 rounded">←</button>
        <div class="font-semibold">{year}년 {month}월</div>
        <button onclick={() => navigateMonth(1)} class="px-2 py-1 text-sm hover:bg-gray-100 rounded">→</button>
      </div>
      <div class="grid grid-cols-7 text-center text-xs font-medium text-gray-500 mb-2">
        {#each ['일', '월', '화', '수', '목', '금', '토'] as d, i}
          <div class={i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : ''}>{d}</div>
        {/each}
      </div>
      <div class="space-y-1">
        {#each weeks as week}
          <div class="grid grid-cols-7 gap-1">
            {#each week as cell, i}
              {#if cell.day == null}
                <div class="h-14"></div>
              {:else}
                {@const meeting = meetingByDate[cell.iso!]}
                {@const isSelected = selected_date === cell.iso}
                {@const isToday = todayIso === cell.iso}
                <button
                  type="button"
                  onclick={() => selectDate(cell.iso)}
                  class="h-14 rounded border text-left p-1 text-xs transition
                    {isSelected ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}
                    {isToday ? 'font-bold' : ''}
                    {i === 0 ? 'text-red-500' : ''}
                    {i === 6 ? 'text-blue-500' : ''}
                  "
                >
                  <div>{cell.day}</div>
                  {#if meeting}
                    <div class="mt-0.5 flex items-center gap-0.5">
                      {#if meeting.cancelled}
                        <span class="px-1 rounded bg-gray-200 text-gray-600 text-[9px]">취소</span>
                      {:else}
                        <span class="px-1 rounded bg-emerald-500 text-white text-[9px]">{meeting.summary.yes}</span>
                        {#if meeting.summary.maybe > 0}<span class="px-1 rounded bg-amber-500 text-white text-[9px]">{meeting.summary.maybe}</span>{/if}
                        {#if meeting.summary.no > 0}<span class="px-1 rounded bg-rose-500 text-white text-[9px]">{meeting.summary.no}</span>{/if}
                      {/if}
                    </div>
                  {/if}
                </button>
              {/if}
            {/each}
          </div>
        {/each}
      </div>

      <!-- 새 모임 추가 -->
      <div class="mt-4 pt-4 border-t">
        <div class="text-xs font-medium text-gray-700 mb-2">새 모임 추가</div>
        <div class="flex gap-2 flex-wrap">
          <input type="date" bind:value={newDate} class="text-xs border rounded px-2 py-1" />
          <input type="text" bind:value={newLabel} placeholder="19:00" class="text-xs border rounded px-2 py-1 w-20" />
          <input type="text" bind:value={newTitle} placeholder="제목 (선택)" class="text-xs border rounded px-2 py-1 flex-1" />
          <button onclick={createMeeting} disabled={!newDate}
                  class="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            추가
          </button>
        </div>
      </div>
    </div>

    <!-- 우: 선택 날짜 출석자 -->
    <div class="lg:col-span-3 bg-white border rounded-lg p-4">
      {#if selected_meeting}
        <div class="flex items-baseline justify-between mb-3">
          <div>
            <div class="text-xs text-gray-500">{selected_meeting.date} {selected_meeting.label ?? ''}</div>
            <h2 class="text-lg font-bold">{selected_meeting.title ?? '모임'}</h2>
            {#if selected_meeting.location}<div class="text-xs text-gray-600 mt-0.5">📍 {selected_meeting.location}</div>{/if}
          </div>
          <div class="flex gap-1.5 text-xs">
            <span class="px-2 py-1 bg-emerald-100 text-emerald-800 rounded">참석 {liveSummary.yes}</span>
            <span class="px-2 py-1 bg-amber-100 text-amber-800 rounded">미정 {liveSummary.maybe}</span>
            <span class="px-2 py-1 bg-rose-100 text-rose-800 rounded">불참 {liveSummary.no}</span>
            <span class="px-2 py-1 bg-gray-100 text-gray-700 rounded">응답없음 {liveSummary.unknown}</span>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-left text-xs text-gray-500 border-b">
              <tr>
                <th class="py-2">참여자</th>
                <th class="py-2">직종</th>
                <th class="py-2">RSVP 상태</th>
                <th class="py-2">답장 메모</th>
              </tr>
            </thead>
            <tbody>
              {#each attendanceState as r (r.response_id)}
                <tr class="border-b last:border-0 hover:bg-gray-50">
                  <td class="py-2">
                    <div class="flex items-center gap-2">
                      {#if r.tier}<span class="w-2 h-2 rounded-full {tierDot[r.tier] ?? 'bg-gray-300'}"></span>{/if}
                      <span class="font-medium">{r.name}</span>
                      {#if r.threads}<span class="text-xs text-gray-500">@{r.threads}</span>{/if}
                      <a
                        href="/admin/community/surveys/vibestudy-pilot-1/responses/{r.response_id}"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="설문 응답 보기 (새 탭)"
                        class="ml-1 px-1.5 py-0.5 text-[10px] border border-gray-200 rounded text-gray-500 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                      >📋 설문</a>
                    </div>
                  </td>
                  <td class="py-2 text-xs text-gray-600 max-w-[180px] truncate" title={r.role ?? ''}>{r.role ?? '-'}</td>
                  <td class="py-2">
                    <div class="flex gap-1">
                      {#each ['yes', 'maybe', 'no', 'unknown'] as v}
                        <button
                          type="button"
                          onclick={() => setRsvp(r, v as any)}
                          class="px-2 py-0.5 text-xs border rounded transition
                                 {r.rsvp === v ? rsvpColors[v] + ' font-semibold' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}"
                        >{rsvpLabels[v].split(' ')[0]}</button>
                      {/each}
                    </div>
                  </td>
                  <td class="py-2 text-xs text-gray-600 max-w-[280px]">
                    {#if r.note}
                      <div class="line-clamp-2" title={r.note}>{r.note}</div>
                      {#if r.responded_at}
                        <div class="text-[10px] text-gray-400 mt-0.5">{new Date(r.responded_at).toLocaleString('ko')}</div>
                      {/if}
                    {:else}
                      <span class="text-gray-300">-</span>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <div class="p-8 text-center text-gray-500">
          ← 좌측 캘린더에서 날짜를 선택하거나 새 모임을 추가하세요.
        </div>
      {/if}
    </div>
  </div>
</div>
